import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { API_URL } from '../config/api';
import { registerPushSubscription } from '../utils/pushNotifications';
import './PushNotificationBanner.css';

const DISMISS_STORAGE_KEY = 'pushPromptDismissedUntil';

/**
 * Shows a banner asking for push notification permission when user is logged in
 * and permission hasn't been granted/denied yet. Rendered in main app layout
 * (not only on Settings) so users are prompted outside Settings too.
 */
const PushNotificationBanner = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setVisible(false);
      return;
    }
    if (!('Notification' in window)) {
      setVisible(false);
      return;
    }
    if (Notification.permission !== 'default') {
      setVisible(false);
      return;
    }
    try {
      const dismissedUntil = localStorage.getItem(DISMISS_STORAGE_KEY);
      if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) {
        setVisible(false);
        return;
      }
    } catch (_) {}
    setVisible(true);
  }, [user]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const result = await registerPushSubscription();
      if (result.ok) {
        const { data } = await axios.put(`${API_URL}/users/settings`, {
          pushNotifications: true
        });
        if (data.settings) {
          updateUser({ ...user, settings: data.settings });
        }
        setVisible(false);
        showSuccess(t('pushBanner.enabled') || 'Notifications enabled. You\'ll get order updates.');
      } else {
        if (result.reason === 'permission') {
          setVisible(false);
          showError(t('pushBanner.permissionDenied') || 'Notifications were denied.');
        } else if (result.reason === 'server') {
          showError(t('pushBanner.notConfigured') || 'Notifications are not available.');
        } else {
          showError(t('pushBanner.failed') || 'Could not enable notifications.');
        }
      }
    } catch (err) {
      showError(err.response?.data?.message || t('pushBanner.failed') || 'Could not enable notifications.');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    try {
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now() + sevenDays));
    } catch (_) {}
  };

  if (!visible) return null;

  return (
    <div className="push-notification-banner" role="region" aria-label={t('pushBanner.ariaLabel') || 'Notification permission'}>
      <p className="push-notification-banner-text">
        {t('pushBanner.message') || 'Get order status updates via browser notifications.'}
      </p>
      <div className="push-notification-banner-actions">
        <button
          type="button"
          className="push-notification-banner-btn push-notification-banner-btn--primary"
          onClick={handleEnable}
          disabled={loading}
        >
          {loading ? (t('pushBanner.enabling') || 'Enabling...') : (t('pushBanner.enable') || 'Enable')}
        </button>
        <button
          type="button"
          className="push-notification-banner-btn push-notification-banner-btn--secondary"
          onClick={handleDismiss}
          disabled={loading}
        >
          {t('pushBanner.notNow') || 'Not now'}
        </button>
      </div>
    </div>
  );
};

export default PushNotificationBanner;
